/**
 * Formulario y ficha de los **movimientos administrativos** (ingresos y egresos).
 *
 * Son las dos ventanas del panel anterior (`create` y `view` de
 * `Administrative_incomes` / `Administrative_expenses`): un formulario con el
 * tipo, la cuenta, el método de pago, el monto, la fecha, la referencia, la
 * descripción y el comprobante —obligatorio en los egresos—, y una ficha de sólo
 * lectura con todo lo que se guardó, incluido el reparto del detalle.
 */

import { FileText } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import type {
  CuentaAdministrativa,
  DetalleAdministrativo,
  MetodoAdministrativo,
  MovimientoAdministrativo,
  SubtipoAdministrativo,
  TipoAdministrativo,
} from '../administrativos.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = 'application/pdf,image/png,image/jpeg';

/** Lo que devuelve el formulario. */
export interface DatosAdministrativos {
  subtipo_id: string;
  cuenta_id: string;
  metodo_pago_id: string;
  monto: string;
  fecha: string;
  referencia: string;
  descripcion: string;
  observaciones: string;
  comprobante: File | null;
}

interface DialogoFormularioProps {
  abierto: boolean;
  tipo: TipoAdministrativo;
  /** Movimiento a editar (si viene, el formulario arranca con sus datos). */
  movimiento: MovimientoAdministrativo | null;
  hoy: string;
  subtipos: SubtipoAdministrativo[];
  cuentas: CuentaAdministrativa[];
  metodos: MetodoAdministrativo[];
  archivoObligatorio: boolean;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (datos: DatosAdministrativos) => void;
}

/** Alta y edición de un movimiento administrativo. */
export function DialogoAdministrativo({
  abierto,
  tipo,
  movimiento,
  hoy,
  subtipos,
  cuentas,
  metodos,
  archivoObligatorio,
  guardando,
  error,
  onCerrar,
  onGuardar,
}: DialogoFormularioProps) {
  const esIngreso = tipo === 'ingresos';
  // El formulario se monta al abrirse, así que arranca con los datos del
  // movimiento a editar (o vacío) sin necesidad de sincronizarlos después.
  const [datos, setDatos] = useState<DatosAdministrativos>(() => ({
    subtipo_id: movimiento?.subtipo_id ? String(movimiento.subtipo_id) : '',
    cuenta_id: movimiento?.cuenta_id ? String(movimiento.cuenta_id) : '',
    metodo_pago_id: movimiento?.metodo_pago_id ? String(movimiento.metodo_pago_id) : '',
    monto: movimiento ? String(movimiento.monto) : '',
    fecha: movimiento?.fecha ?? hoy,
    referencia: movimiento?.referencia ?? '',
    descripcion: movimiento?.descripcion ?? '',
    observaciones: movimiento?.observaciones ?? '',
    comprobante: null,
  }));

  const monto = Number(datos.monto.replace(',', '.')) || 0;
  const tieneComprobante = Boolean(movimiento?.comprobante) || datos.comprobante !== null;
  const puedeGuardar =
    Boolean(datos.subtipo_id) &&
    Boolean(datos.cuenta_id) &&
    Boolean(datos.metodo_pago_id) &&
    monto > 0 &&
    Boolean(datos.fecha) &&
    datos.descripcion.trim().length > 0 &&
    (!archivoObligatorio || tieneComprobante);

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title={
        movimiento
          ? `Editar ${esIngreso ? 'ingreso' : 'egreso'} #${movimiento.id}`
          : `Nuevo ${esIngreso ? 'ingreso' : 'egreso'} administrativo`
      }
      description={
        esIngreso
          ? 'Dinero que entra a una cuenta sin ser una venta: préstamos recibidos, intereses, alquileres cobrados…'
          : 'Dinero que sale de una cuenta: planilla, servicios, alquileres, impuestos, honorarios…'
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar}
            onClick={() =>
              onGuardar({
                ...datos,
                monto: monto.toFixed(2),
                descripcion: datos.descripcion.trim(),
                referencia: datos.referencia.trim(),
                observaciones: datos.observaciones.trim(),
              })
            }
          >
            {movimiento ? 'Guardar cambios' : `Registrar ${esIngreso ? 'ingreso' : 'egreso'}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="admin-subtipo">
              Tipo de {esIngreso ? 'ingreso' : 'egreso'}
            </label>
            <Select
              id="admin-subtipo"
              value={datos.subtipo_id}
              onChange={(evento) => setDatos({ ...datos, subtipo_id: evento.target.value })}
              className={CONTROL}
              aria-label="Tipo de movimiento"
            >
              <option value="">Seleccionar…</option>
              {subtipos.map((subtipo) => (
                <option key={subtipo.id} value={String(subtipo.id)}>
                  {subtipo.nombre}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="admin-cuenta">
              Cuenta bancaria
            </label>
            <Select
              id="admin-cuenta"
              value={datos.cuenta_id}
              onChange={(evento) => setDatos({ ...datos, cuenta_id: evento.target.value })}
              className={CONTROL}
              aria-label="Cuenta bancaria"
            >
              <option value="">Seleccionar…</option>
              {cuentas.map((cuenta) => (
                <option key={cuenta.id} value={String(cuenta.id)}>
                  {cuenta.etiqueta}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="admin-metodo">
              Método de pago
            </label>
            <Select
              id="admin-metodo"
              value={datos.metodo_pago_id}
              onChange={(evento) => setDatos({ ...datos, metodo_pago_id: evento.target.value })}
              className={CONTROL}
              aria-label="Método de pago"
            >
              <option value="">Seleccionar…</option>
              {metodos.map((metodo) => (
                <option key={metodo.id} value={String(metodo.id)}>
                  {metodo.nombre}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="Monto (S/)"
            type="number"
            step="0.01"
            min="0.01"
            value={datos.monto}
            onChange={(evento) => setDatos({ ...datos, monto: evento.target.value })}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="admin-fecha">
              Fecha del movimiento
            </label>
            <CampoFecha
              id="admin-fecha"
              tipo="date"
              value={datos.fecha}
              onChange={(evento) => setDatos({ ...datos, fecha: evento.target.value })}
              aria-label="Fecha del movimiento"
            />
          </div>

          <Input
            label="Referencia (opcional)"
            value={datos.referencia}
            placeholder="Nro. de operación, documento…"
            onChange={(evento) => setDatos({ ...datos, referencia: evento.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="admin-descripcion">
            Descripción
          </label>
          <textarea
            id="admin-descripcion"
            rows={3}
            maxLength={500}
            className={cn(CONTROL, 'h-auto py-2')}
            value={datos.descripcion}
            placeholder="Detalle del movimiento"
            onChange={(evento) => setDatos({ ...datos, descripcion: evento.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="admin-observaciones">
            Observaciones (opcional)
          </label>
          <textarea
            id="admin-observaciones"
            rows={2}
            maxLength={1000}
            className={cn(CONTROL, 'h-auto py-2')}
            value={datos.observaciones}
            onChange={(evento) => setDatos({ ...datos, observaciones: evento.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <ArchivoInput
            label={`Comprobante ${archivoObligatorio ? '' : '(opcional)'}`}
            accept={ACEPTADOS}
            nombre={datos.comprobante?.name ?? null}
            hint={`PDF, PNG, JPG o JPEG de hasta 5 MB. Lo guarda la API y se sirve con la sesión.${
              archivoObligatorio ? ' En los egresos es obligatorio.' : ''
            }`}
            onSeleccionar={(elegido) =>
              setDatos({ ...datos, comprobante: elegido?.archivo ?? null })
            }
          />
          {movimiento?.comprobante ? (
            <p className="text-xs text-ink-500">
              Comprobante actual: {movimiento.comprobante.archivo}
              {datos.comprobante ? ' (se reemplazará)' : ''}
            </p>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

/** Un dato de la ficha, con su etiqueta. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm text-ink-800">{valor}</span>
    </div>
  );
}

interface DialogoDetalleProps {
  abierto: boolean;
  tipo: TipoAdministrativo;
  consulta: DetalleAdministrativo | undefined;
  cargando: boolean;
  onCerrar: () => void;
}

/** Ficha de un movimiento administrativo. */
export function DialogoDetalleAdministrativo({
  abierto,
  tipo,
  consulta,
  cargando,
  onCerrar,
}: DialogoDetalleProps) {
  const movimiento = consulta?.movimiento;
  const esIngreso = tipo === 'ingresos';
  const [error, setError] = useState<string | null>(null);

  const abrir = async (url: string, nombre: string) => {
    setError(null);

    try {
      await abrirPdfAutenticado(url, { nombre });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title={movimiento ? `Movimiento #${movimiento.id}` : 'Movimiento'}
      description={
        movimiento ? `${movimiento.subtipo} · ${movimiento.banco} · ${movimiento.cuenta}` : undefined
      }
      size="lg"
      footer={
        <>
          {movimiento?.comprobante ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<FileText className="size-4" aria-hidden="true" />}
              disabled={!movimiento.comprobante.disponible}
              title={
                movimiento.comprobante.disponible
                  ? 'Ver el comprobante'
                  : 'El archivo ya no está en el servidor'
              }
              onClick={() =>
                movimiento.comprobante
                  ? void abrir(movimiento.comprobante.url, movimiento.comprobante.archivo)
                  : undefined
              }
            >
              Ver comprobante
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={onCerrar}>
            Cerrar
          </Button>
        </>
      }
    >
      {cargando ? <p className="text-sm text-ink-500">Cargando…</p> : null}

      {error ? <Alert variant="error">{error}</Alert> : null}

      {movimiento ? (
        <div className="flex flex-col gap-4">
          {movimiento.anulado ? (
            <Alert variant="warning" title="Movimiento anulado">
              Lo anuló {movimiento.anulado_por ?? 'el sistema'}
              {movimiento.anulado_el ? ` el ${movimiento.anulado_el}` : ''}.
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={esIngreso ? 'success' : 'danger'}>
              {esIngreso ? 'Ingreso' : 'Egreso'}
            </Badge>
            <Badge variant={movimiento.anulado ? 'neutral' : 'brand'}>{movimiento.estado}</Badge>
            <span
              className={cn(
                'ml-auto text-lg font-bold tabular-nums',
                movimiento.anulado ? 'text-ink-400 line-through' : esIngreso ? 'text-emerald-700' : 'text-rose-600',
              )}
            >
              {formatCurrency(movimiento.monto)}
            </span>
          </div>

          <section className="grid gap-3 rounded-card border border-ink-100 p-4 sm:grid-cols-2">
            <Dato etiqueta="Tipo" valor={movimiento.subtipo} />
            <Dato
              etiqueta="Fecha del movimiento"
              valor={movimiento.fecha ? formatDateValue(movimiento.fecha) : '—'}
            />
            <Dato etiqueta="Banco" valor={movimiento.banco} />
            <Dato
              etiqueta="Cuenta"
              valor={`${movimiento.cuenta}${movimiento.cuenta_numero ? ` · ${movimiento.cuenta_numero}` : ''}`}
            />
            <Dato etiqueta="Método de pago" valor={movimiento.metodo_pago} />
            <Dato etiqueta="Referencia" valor={movimiento.referencia || '—'} />
            <Dato etiqueta="Registró" valor={movimiento.ejecutor} />
            <Dato etiqueta="Registrado" valor={movimiento.registrado ?? '—'} />
          </section>

          <section className="rounded-card border border-ink-100 p-4">
            <h3 className="text-sm font-semibold text-ink-800">Descripción</h3>
            <p className="mt-1 text-sm text-ink-600">{movimiento.descripcion || '—'}</p>
          </section>

          {movimiento.observaciones ? (
            <section className="rounded-card border border-ink-100 p-4">
              <h3 className="text-sm font-semibold text-ink-800">Observaciones</h3>
              <p className="mt-1 text-sm text-ink-600">{movimiento.observaciones}</p>
            </section>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
